// Shared double-entry posting helpers.
//
// Small, dependency-free utilities used by any page that needs to write a
// balanced journal entry to the ledger (fixed-asset acquisition/disposal,
// tax-liability settlement, …). Mirrors the inline pattern used by the
// payroll and depreciation runs.

import { supabase } from '@/lib/supabase';

export interface PostLine {
  account_id: string;
  debit: number;
  credit: number;
  description: string;
}

/** Round to 2 dp (currency). */
export const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Local-timezone YYYY-MM-DD (defaults to today). Ledger dates must NEVER go
 * through `toISOString()` — Botswana is UTC+2, so between 00:00 and 02:00 the
 * UTC date is still *yesterday*, and on the 1st of the month that dates the
 * posting into the PREVIOUS month, silently excluding it from the dashboard's
 * month-start→now window and the VAT/PAYE period.
 */
export function localYmd(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** gl_code → account id for one business. */
export async function fetchAccountMap(businessId: string): Promise<Map<string, string>> {
  const { data } = await supabase
    .from('accounts')
    .select('id, gl_code')
    .eq('business_id', businessId);
  const m = new Map<string, string>();
  for (const a of (data ?? []) as Array<{ id: string; gl_code: string }>) m.set(a.gl_code, a.id);
  return m;
}

/**
 * Post a balanced journal entry, dropping zero-value lines (the journal_lines
 * CHECK requires exactly one of debit/credit to be > 0). Idempotent: if an
 * entry already exists for this (business, source, reference [, source_id])
 * it is left untouched and `false` is returned. Returns `true` when a new
 * entry was posted.
 */
export async function postJournalEntry(args: {
  businessId: string;
  date: string;
  reference: string;
  description: string;
  source: 'sale' | 'purchase' | 'adjustment' | 'bank';
  sourceId: string | null;
  createdBy: string | null;
  lines: PostLine[];
}): Promise<boolean> {
  // Drop zero lines (the journal_lines CHECK needs exactly one side > 0) and
  // assert the entry balances BEFORE we write the header — this prevents an
  // orphaned, unbalanced header from ever being committed (Round-2 finding #1).
  const lines = args.lines.filter((l) => l.debit > 0 || l.credit > 0);
  const totDebit = r2(lines.reduce((s, l) => s + l.debit, 0));
  const totCredit = r2(lines.reduce((s, l) => s + l.credit, 0));
  if (lines.length < 2 || totDebit !== totCredit || totDebit <= 0) {
    throw new Error(
      `Refusing to post unbalanced entry "${args.reference}": Dr ${totDebit} ≠ Cr ${totCredit}`,
    );
  }

  let dupe = supabase
    .from('journal_entries')
    .select('id')
    .eq('business_id', args.businessId)
    .eq('source', args.source)
    .eq('reference', args.reference);
  if (args.sourceId) dupe = dupe.eq('source_id', args.sourceId);
  const { data: existing } = await dupe.maybeSingle();
  if (existing) return false;

  const { data: entry, error: entryErr } = await supabase
    .from('journal_entries')
    .insert({
      business_id: args.businessId,
      entry_date: args.date,
      reference: args.reference,
      description: args.description,
      source: args.source,
      source_id: args.sourceId,
      is_posted: true,
      posted_at: new Date().toISOString(),
      created_by: args.createdBy,
    })
    .select()
    .single();
  if (entryErr) throw entryErr;
  if (!entry) return false;
  const eid = (entry as { id: string }).id;

  const { error: linesErr } = await supabase.from('journal_lines').insert(
    lines.map((l) => ({
      entry_id: eid,
      account_id: l.account_id,
      debit: l.debit,
      credit: l.credit,
      vat_amount: 0,
      vat_rate: 0,
      description: l.description,
    })),
  );
  // If the lines fail, the header is already committed (separate request) —
  // remove the orphan so it can't corrupt the trial balance, then surface it.
  if (linesErr) {
    await supabase.from('journal_entries').delete().eq('id', eid);
    throw linesErr;
  }
  return true;
}
